<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getWebAppsPath } from "@core/schema/paths/getWebAppsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsGastro } from "../data/getFaqsGastro.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsGastro } from "../data/getReasonsGastro.js";
	import { getWhatWeDevelopGastro } from "../data/getWhatWeDevelopGastro.js";
	import type { GastroProps } from "./GastroProps.js";

	const { partners }: GastroProps = $props();
	const { tenant } = getGlobalTemplateProps();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="V hotelech i gastru dostáváme procesy nl[[]] cg[[„pod jednu střechu“]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Provozujete sp[[restauraci]], sp[[hotel]] nebo třeba sp[[kavárnu]]? sp[[100% na míru]] vám vytvoříme sp[[pokladní]] či sp[[objednávkový]] systém, pomůžeme sp[[digitalizovat]] rutinní procesy nebo sjednotíme veškerou firemní agendu sp[[„pod jednu střechu“]]."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Zeptat se na podrobnosti" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" gridDesc="container-6" centered>
		{#snippet headline()}
			<Formatted text="cg[[Zrychlíme]], cg[[zjednodušíme]] a cg[[sjednotíme]] váš gastro nebo hotelový provoz" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Zmatky v objednávkách, nepřehledná komunikace a komplikované procesy]] jsou problémy, které brzdí rozvoj stovek českých hotelů, restaurací, kaváren a dalších podobných podniků. sp[[Ten váš k nim patřit nebude.]]"
				/>
			</p>
			<p>
				<Formatted
					text="Ve Fastest vám sp[[100% na míru]] připravíme sp[[uživatelsky přívětivý pokladní či objednávkový systém nebo vlastní [{getWebAppsPath(
						tenant,
					)}][[webovou]]]] sp[[i mobilní aplikaci]]. V oblasti HORECA máme zkušenosti také s digitalizací a automatizací firemních procesů i s jejich sjednocováním tzv. pod jednu střechu. sp[[Co pro vás mimo jiné zajistíme?]]"
				/>
			</p>
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getWhatWeDevelopGastro(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Poslat poptávku
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="cg[[Software]] pro hotely, restaurace, kavárny nebo cateringy cg[[vyvíjíme dlouhodobě]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="S firmami z odvětví cg[[HORECA]] spolupracujeme prakticky od našeho cg[[vzniku]]. cg[[Nejčastěji]] vyvíjíme software nebo digitalizujeme a automatizujeme procesy cg[[pro:]]"
				/>
			</p>
			<div class="flex flex-col md:flex-row md:gap-16">
				<ul class="custom-list">
					<li><Formatted text="Hotely a ubytovací zařízení" /></li>
					<li><Formatted text="Restaurace" /></li>
					<li><Formatted text="Kavárny a bary" /></li>
					<li><Formatted text="Jídelny a cateringy" /></li>
				</ul>
			</div>
		{/snippet}
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="cg[[Sladké dílně]] jsme pomohli zvýšit počet objednávek cg[[o 50 %]]" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.Gastro} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="Kdo další cg[[využil]] služeb Fastest?" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Gastro)} />

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] se jako hotel či gastro podnik pustit do cg[[spolupráce]] s cg[[Fastest]]" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getReasonsGastro(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k řešením v oblasti cg[[HORECA]]" />
		{/snippet}
		<FaqItems faqs={getFaqsGastro(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
